import type {
  ChatMessage,
  ContentAudio,
  ContentData,
  ContentDocument,
  ContentImage,
  ContentReasoning,
  ContentText,
  ContentToolUse,
  ContentVideo,
} from "@tsmono/inspect-common/types";

export interface MessagesToStrOptions {
  excludeSystem?: boolean;
  excludeToolUsage?: boolean;
  excludeReasoning?: boolean;
}

export const messagesToStr = (
  messages: ChatMessage[],
  options?: MessagesToStrOptions
): string => {
  const opts = options || {};
  return messages
    .map((msg) => messageToStr(msg, opts))
    .filter((str): str is string => str !== null)
    .join("\n");
};

const messageToStr = (
  message: ChatMessage,
  options: MessagesToStrOptions
): string | null => {
  // Exclude system messages if requested
  if (options.excludeSystem && message.role === "system") {
    return null;
  }

  const content = betterContentText(
    message.content,
    options.excludeToolUsage || false,
    options.excludeReasoning || false
  );

  // Handle assistant messages with tool calls
  if (
    !options.excludeToolUsage &&
    message.role === "assistant" &&
    message.tool_calls
  ) {
    let entry = `${message.role.toUpperCase()}:\n${content}\n`;

    for (const tool of message.tool_calls) {
      const funcName = tool.function;
      const args = tool.arguments;

      const argsText = Object.entries(args)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join("\n");
      entry += `\nTool Call: ${funcName}\nArguments:\n${argsText}\n`;
    }

    return entry;
  }

  // Handle tool messages
  if (message.role === "tool") {
    if (options.excludeToolUsage) {
      return null;
    }
    const funcName = message.function || "unknown";
    const errorPart = message.error
      ? `\n\nError in tool call '${funcName}':\n${message.error.message}\n`
      : "";
    return `${message.role.toUpperCase()}:\n${content}${errorPart}\n`;
  }

  // Default formatting for system, user, and assistant messages without tool calls
  return `${message.role.toUpperCase()}:\n${content}\n`;
};

type ContentItem =
  | ContentText
  | ContentReasoning
  | ContentImage
  | ContentAudio
  | ContentVideo
  | ContentData
  | ContentToolUse
  | ContentDocument;

const textFromContent = (
  content: ContentItem,
  excludeToolUsage: boolean,
  excludeReasoning: boolean
): string | null => {
  switch (content.type) {
    case "text":
      return content.text;

    case "reasoning": {
      if (excludeReasoning) {
        return null;
      }
      const text = content.redacted
        ? content.summary
        : content.reasoning || content.summary;
      if (!text) {
        return null;
      }
      return `\n<think>${text}</think>`;
    }

    case "tool_use": {
      if (excludeToolUsage) {
        return null;
      }
      const errorStr = content.error ? ` ${content.error}` : "";
      return `\nTool Use: ${content.name}(${content.arguments}) -> ${content.result}${errorStr}`;
    }

    case "image":
    case "audio":
    case "video":
    case "data":
    case "document":
      return `<${content.type} />`;

    default:
      return null;
  }
};

const betterContentText = (
  content: string | ContentItem[],
  excludeToolUsage: boolean,
  excludeReasoning: boolean
): string => {
  if (typeof content === "string") {
    return content;
  }

  const allText = content
    .map((c) => textFromContent(c, excludeToolUsage, excludeReasoning))
    .filter((text): text is string => text !== null);

  return allText.join("\n");
};
